import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apibooKey } from '../services/storageNamespace';
import { rehydrateOnAccountChange } from '../services/accountScopedPersist';
import { isDesktopOnlyPanel, isWebApp } from '../platform/target';
import {
  isBundledExtension,
  isShippedBundledExtension,
  hasAllOrNothingCapabilities,
} from './bundledIds';
import { normalizeRailHidden, isLastVisible, railLeaderOf, toggleRailId } from '../onboarding/railIds';

/**
 * ── Where the persisted halves live ───────────────────────────────────────
 *
 * Rust already drew this line for the same reason: an extension's BUNDLE lives
 * device-wide under `extensions/<slug>/`, while its `storage.json` and its
 * keychain entries are keyed by account. Code is device-global; data and
 * consent are not. The browser half now matches:
 *
 *   apiboo-extensions-installed      DEVICE-GLOBAL — `installedFromMarketplace`
 *     reduced to {manifest, version, bundlePath}: which bundles this device
 *     downloaded and where they sit on disk. It is an inventory of code, it
 *     mirrors a device-global directory, and if it were per-account the cached
 *     bundle would be invisible (and un-updatable, and never cleaned up) to
 *     every account but the installer's.
 *
 *   apiboo-<accountId>-extensions    PER-ACCOUNT — `grantedCapabilities`,
 *     `revokedCapabilities`, `knownManifestCaps`, `enabled`,
 *     `dismissedExtensions`. Every one of these is a SECURITY DECISION a
 *     specific person made: granting `db:postgres` or keychain access is
 *     consent, and consent does not transfer to the next person who signs in
 *     on this machine. An account that has never seen an extension starts with
 *     no grants and makes its own call. `railHidden` (which rail buttons this
 *     person hid) rides along: a preference rather than consent, but theirs.
 *
 * That is why `enabled` and `grantedCapabilities` are also STRIPPED out of
 * each install record on the way to the device-global key: they were a second
 * copy of exactly the state that must not be shared (bootstrap.js used them as
 * a fallback, which would have re-granted A's permissions to B on B's first
 * launch). The record keeps only what identifies the code.
 */
const DEVICE_INSTALLS_KEY = 'apiboo-extensions-installed';
const ACCOUNT_SUFFIX = 'extensions';

/** Persisted fields that belong to the DEVICE; everything else is per-account. */
const DEVICE_FIELDS = new Set(['installedFromMarketplace']);

/** The persisted per-account slice at its "never seen this device" defaults. */
const EMPTY_ACCOUNT_SLICE = {
  enabled: {},
  grantedCapabilities: {},
  revokedCapabilities: {},
  knownManifestCaps: {},
  dismissedExtensions: {},
  railHidden: [],
  apiClientEnabled: true,
};

function readJSON(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore (quota / disabled storage)
  }
}

/** An install record with the consent fields removed — code identity only. */
function codeIdentityOnly(records) {
  const out = {};
  for (const [slug, record] of Object.entries(records || {})) {
    if (!record) continue;
    out[slug] = { manifest: record.manifest, version: record.version, bundlePath: record.bundlePath };
  }
  return out;
}

/**
 * One store, two keys. persist() hands us the whole `{state, version}` blob;
 * we split it on the way out and glue it back together on the way in, so the
 * device-global inventory and the per-account consent state can never end up
 * in the same key by accident.
 */
const splitStorage = {
  getItem: () => {
    const device = readJSON(DEVICE_INSTALLS_KEY);
    const account = readJSON(apibooKey(ACCOUNT_SUFFIX));
    if (!device && !account) return null;
    return JSON.stringify({
      version: account?.version ?? device?.version,
      state: { ...(device?.state || {}), ...(account?.state || {}) },
    });
  },
  setItem: (_name, value) => {
    let parsed;
    try {
      parsed = JSON.parse(value);
    } catch {
      return;
    }
    const state = parsed?.state || {};
    const deviceState = {};
    const accountState = {};
    for (const [field, fieldValue] of Object.entries(state)) {
      if (DEVICE_FIELDS.has(field)) deviceState[field] = fieldValue;
      else accountState[field] = fieldValue;
    }
    deviceState.installedFromMarketplace = codeIdentityOnly(deviceState.installedFromMarketplace);
    writeJSON(DEVICE_INSTALLS_KEY, { ...parsed, state: deviceState });
    writeJSON(apibooKey(ACCOUNT_SUFFIX), { ...parsed, state: accountState });
  },
  removeItem: () => {
    try {
      localStorage.removeItem(apibooKey(ACCOUNT_SUFFIX));
    } catch {
      // ignore
    }
  },
};

/**
 * Extension install/enable/permission state, plus which extension panels are
 * currently "activated" (mounted somewhere in the tree, possibly hidden —
 * see ExtensionHost.js's freeze/idle-teardown lifecycle). A panel staying in
 * `activatedPanels` is what keeps its iframe alive across a tab switch long
 * enough to freeze-then-idle-teardown instead of unmounting immediately;
 * removing it from this set is the trigger that actually unmounts the host
 * component and releases the iframe's memory.
 *
 * Persistence (see `partialize`, and the split described above): the device's
 * install records plus this account's enable/grant/revoke/uninstall state.
 * Everything else here is rebuilt fresh on every launch by bootstrap.js (from
 * the bundled fallback and/or a background marketplace sync), so persisting it
 * would just risk it going stale against the actual registered
 * manifest/entrySource.
 */
export const useExtensionStore = create(
  persist(
    (set, get) => ({
      extensions: {}, // id -> manifest
      enabled: {}, // id -> boolean
      grantedCapabilities: {}, // id -> string[]
      // id -> string[] — capabilities the user EXPLICITLY revoked in Settings.
      // A revoke is only represented as absence from `grantedCapabilities`,
      // which is indistinguishable from "the manifest didn't declare this cap
      // when the grants were persisted" (staleness). bootstrap.js reconciles
      // newly-declared bundled-manifest capabilities into stale persisted
      // grants, and this map is what protects a deliberate revoke from being
      // silently re-granted by that reconciliation. Persisted.
      revokedCapabilities: {}, // id -> string[]
      // id -> string[] — snapshot of the manifest-declared capabilities last
      // seen for an extension. bootstrap.js diffs the current manifest against
      // this to find capabilities added by an app update (e.g. DB Manager
      // growing db:postgres/db:sqlite after profiles persisted a MySQL-only
      // grant set). Until this baseline exists for an extension, persisted
      // grants are authoritative and nothing is unioned in — a missing cap on
      // a pre-snapshot profile may be a revoke made before revokedCapabilities
      // existed, and must not be silently re-granted. Persisted.
      knownManifestCaps: {}, // id -> string[]
      activatedPanels: {}, // panelId -> true
      treeData: {}, // sectionId -> TreeNode[]
      entrySources: {}, // extensionId -> guest bundle source string
      pendingModal: null, // { extensionId, requestId, spec } | null — see ExtensionModalHost.js
      // slug -> { manifest, version, bundlePath } — the device's inventory of
      // downloaded extension code, persisted DEVICE-GLOBALLY (it mirrors the
      // device-global bundle directory Rust owns). bootstrap.js reads this to
      // re-register a cached marketplace-downloaded version (via the stored
      // manifest + bundlePath/readTextFile) instead of falling back to the
      // bundled copy every launch. The manifest itself must be persisted here
      // (not re-derived from the bundled fallback) since a marketplace version's
      // contributes/permissions can differ from the bundled manifest.
      // NOTE: no grants and no enable flag — a record shared by every account
      // on the device must not carry one account's consent decisions.
      installedFromMarketplace: {},
      // id -> true — set by unregisterExtension, cleared by registerExtension.
      // THIRD-PARTY ONLY. Uninstalling a marketplace extension deletes its
      // bundle from disk, and this flag is what stops bootstrap.js re-adding
      // it from a cached record and stops syncMarketplace() silently
      // reinstalling it on the next update check. Both check it before acting.
      //
      // It must NEVER hold a bundled id. Bundled code lives in the binary and
      // cannot be deleted, so a dismissal there did not uninstall anything —
      // it just hid the extension forever. unregisterExtension now refuses
      // bundled ids, and undismissBundled below clears any such flag left by
      // the build that did allow it.
      dismissedExtensions: {},
      // Rail ids (see onboarding/railIds.js) this account HID from the
      // activity rail — the onboarding role picker and Settings → Your tools.
      // Hiding is NOT disabling: the extension stays enabled and mounted, so
      // shortcuts, deep links and openHelp() still reach a hidden tool. Only
      // ActivityBar reads this. Persisted per account.
      railHidden: [],

      // The API Client (and History with it — one switch, owner 28.10.2026)
      // switched off in Settings → Extensions; the rule is
      // extensions/apiClientSwitch.js. Read it through `selectApiClientEnabled`
      // / `isApiClientEnabled` below: a missing value (a profile saved before
      // the key existed) means ON.
      apiClientEnabled: true,
      setApiClientEnabled: (on) => set({ apiClientEnabled: on !== false }),

      /** Replace the hidden set. At least one tool always stays visible. */
      setRailHidden: (ids, available) => {
        set({ railHidden: normalizeRailHidden(ids, available) });
      },

      /**
       * Hide or show one rail tool; refuses to hide the last visible one. The
       * API Client takes History with it, and History cannot come back while
       * the API Client is off (railIds.RAIL_FOLLOWERS).
       */
      toggleRailHidden: (id, available) => {
        const current = get().railHidden || [];
        if (isLastVisible(current, id, available)) return;
        const leader = railLeaderOf(id);
        if (leader && current.includes(leader) && current.includes(id)) return;
        set({ railHidden: normalizeRailHidden(toggleRailId(current, id), available) });
      },

      /**
       * One-shot migration for state a shipped defect left behind.
       *
       * Bundled extensions used to offer an "Uninstall" button that could not
       * uninstall anything — the code is compiled into the binary. All it did
       * was set `dismissedExtensions[id]`, which bootstrap.js honours forever,
       * so the extension disappeared from the app permanently. Bundled
       * extensions are enable/disable-only now, which means those stale flags
       * have no surface that could ever clear them: without this, an upgrading
       * user who once pressed Uninstall would be stuck with the extension gone
       * and no way back.
       *
       * The old dismissal did exactly one thing the user could observe: it hid
       * the extension. Its honest equivalent under the new model is DISABLED —
       * so the intent is TRANSLATED, not discarded. The extension comes back
       * present-but-off: visible in Settings > Extensions with its switch
       * turned off, one click from returning, and still absent from the
       * sidebar/activity bar until the user asks for it. Silently re-enabling
       * it instead would override a decision the user actually made.
       *
       * Idempotent, and genuinely does not call `set` once there is nothing
       * stale left — this runs on every launch. The check has to happen OUTSIDE
       * the updater: zustand's persist wrapper fires `setItem()` after every
       * `set`, whether or not the updater returned a changed state, so a
       * `return state` early-out inside would still write to storage on every
       * boot.
       *
       * `|| {}` is not defensive noise. This runs at module scope from
       * `bootstrapExtensions()`, before anything renders, and reads a persisted
       * blob a user can hand-edit — `dismissedExtensions: null` would otherwise
       * throw here and nothing would render at all. `bootstrap.js` documents
       * exactly that guarantee for its own loop; this must not be the hole in it.
       */
      undismissBundled: (bundledIds) => {
        const current = get().dismissedExtensions || {};
        if (!bundledIds.some((id) => current[id])) return;

        set((state) => {
          const dismissed = state.dismissedExtensions || {};
          const stale = bundledIds.filter((id) => dismissed[id]);
          if (stale.length === 0) return state;
          const dismissedExtensions = { ...dismissed };
          const enabled = { ...state.enabled };
          for (const id of stale) {
            delete dismissedExtensions[id];
            // Only fill in the disable where the user has no live preference.
            // unregisterExtension deleted `enabled[id]` on its way out, so a
            // value being present means a later, deliberate choice that must
            // not be stomped by a migration.
            if (enabled[id] === undefined) enabled[id] = false;
          }
          return { dismissedExtensions, enabled };
        });
      },

      /**
       * One-shot migration for state a removed UI can no longer express — the
       * same shape as `undismissBundled` above, and for the same reason.
       *
       * Settings used to render a toggle per declared capability. For an
       * extension whose capabilities are all-or-nothing that switch could only
       * ever break it, so it is gone and the extension's own enable switch is
       * the single control. But `revokedCapabilities` is persisted PER ACCOUNT
       * and bootstrap.js honours it on every launch, so a user who had already
       * revoked, say, `db:postgres` would upgrade into a DB Manager that fails
       * on every postgres connection with no control left anywhere to grant it
       * back. That is exactly the `dismissedExtensions` trap again.
       *
       * Two things happen together, and which one is which matters:
       *
       *   - the stale revoke is DROPPED, because nothing can express it or
       *     clear it any more; and
       *   - the extension is TURNED OFF.
       *
       * Turning it off, rather than quietly handing the capability back, is the
       * entire point. That revoke is a refusal the user really made, and
       * silently re-granting keychain or database access is precisely what
       * `reconcileBundledGrants` in bootstrap.js exists to prevent — "a
       * permission system must resolve that ambiguity toward consent, not
       * availability". So the refusal is honoured at the only granularity that
       * still exists (the extension is off) and the capabilities are NOT
       * re-granted here. They come back the moment the user flips the switch
       * on, which under the new model is the consent act — see `setEnabled`.
       *
       * WHAT THAT USER SEES AFTER UPGRADING: DB Manager still listed in
       * Settings > Extensions, switched OFF, one click from working fully
       * again. Never a half-working extension, and never a silent re-grant.
       *
       * Note the deliberate difference from `undismissBundled`, which only
       * fills in a MISSING enable value: here the disable is unconditional.
       * The revoke IS the live preference being translated, and it was almost
       * always made on an extension the user had left enabled — so "only if
       * unset" would translate nothing at all in the case that matters.
       *
       * Idempotent, and the emptiness check sits OUTSIDE the updater for the
       * same reason `undismissBundled`'s does: zustand's persist wrapper fires
       * `setItem()` after every `set`, changed or not, and this runs on every
       * launch. `|| {}` guards a hand-edited persisted blob, as there.
       */
      migrateAllOrNothingRevokes: (manifests) => {
        // Keyed on SHIPPED-BUNDLED, not on `hasAllOrNothingCapabilities`.
        //
        // It used to be the latter, which meant "compiled-in AND declaring at
        // least one capability". As of 05.08.2026 no bundled extension declares
        // any (db-manager, the last one that did, is labelled hostRendered and
        // exempt from the capability gate), so that filter matches nothing and
        // this migration would have quietly become a no-op — while the state it
        // migrates is MORE stranded than before, not less: with the gate exempt,
        // a persisted revoke is no longer even enforced, so a profile upgrading
        // straight from a pre-03.08.2026 build would silently get back the
        // keychain and database access it had explicitly refused. That is the
        // "wrong fix (2)" bundledCapabilityGranularity.test.js is built to
        // catch, and it would have shipped as an accident of a filter.
        //
        // The real question this asks is "could any surface still express or
        // clear this revoke?", and for every compiled-in first-party extension
        // the answer is no — Settings shows one switch and the gate does not
        // consult grants at all. That set is exactly `isShippedBundledExtension`,
        // and it is a superset of the old filter, so nothing that used to
        // migrate stops migrating. The dev half is excluded for the same reason
        // it is excluded everywhere else: dev/localExtensions.js honours an
        // explicit grant list, and its toggles still work.
        const affected = (manifests || []).filter((m) => m && isShippedBundledExtension(m.id));
        const current = get().revokedCapabilities || {};
        const stale = affected.filter((m) => (current[m.id] || []).length > 0);
        if (stale.length === 0) return;

        set((state) => {
          const revokedCapabilities = { ...(state.revokedCapabilities || {}) };
          const enabled = { ...state.enabled };
          for (const m of stale) {
            delete revokedCapabilities[m.id];
            enabled[m.id] = false;
          }
          return { revokedCapabilities, enabled };
        });
      },

      publishTree: (sectionId, nodes) => {
        set((state) => ({ treeData: { ...state.treeData, [sectionId]: nodes } }));
      },

      // The guest is gone (idle teardown / unmount) and so are its native DB
      // connections, but its last published tree stays on screen until a new
      // guest republishes. Drop every arrowTone from those trees, and turn
      // every status dot back to idle (its own idleAction/idleTooltip), so no
      // root keeps saying "connected" for a connection that no longer exists
      // (card 802fc3d9; the dot replaced the green chevron 06.10.2026).
      clearArrowTones: (sectionIds) => {
        const strip = (list) =>
          Array.isArray(list)
            ? list.map((n) => {
                if (!n || typeof n !== 'object') return n;
                const { arrowTone, ...rest } = n;
                if (rest.status && rest.status.state !== 'disconnected') {
                  rest.status = { ...rest.status, state: 'disconnected', action: rest.status.idleAction || null, tooltip: rest.status.idleTooltip || '' };
                }
                if (Array.isArray(rest.children)) rest.children = strip(rest.children);
                if (Array.isArray(rest.resolvedChildren)) rest.resolvedChildren = strip(rest.resolvedChildren);
                return rest;
              })
            : list;
        set((state) => {
          const treeData = { ...state.treeData };
          for (const id of sectionIds) if (treeData[id]) treeData[id] = strip(treeData[id]);
          return { treeData };
        });
      },

      // sdk.tree.expand: a one-shot request ExtensionTreeSection consumes
      // (adds the ids to its expanded set, then clears the request).
      treeExpandRequests: {},
      requestTreeExpand: (sectionId, ids) => {
        set((state) => ({ treeExpandRequests: { ...state.treeExpandRequests, [sectionId]: ids } }));
      },
      clearTreeExpand: (sectionId) => {
        set((state) => {
          if (!state.treeExpandRequests[sectionId]) return state;
          const next = { ...state.treeExpandRequests };
          delete next[sectionId];
          return { treeExpandRequests: next };
        });
      },

      /**
       * Claims the single global modal slot for `extensionId`.
       *
       * There is exactly one slot, so a bare `set()` here meant any extension
       * could silently replace the dialog another extension had already put
       * in front of the user — the swap that turns an app-native modal into a
       * credential-phishing overlay (the victim's real "New connection"
       * dialog is replaced by a pixel-identical fake between the user
       * triggering it and reading it). Refuse instead: the incoming request
       * is told no and its promise rejects, and the modal the user is already
       * looking at stays the one they were looking at.
       *
       * Re-entry by the SAME extension is allowed (its own multi-step flows
       * legitimately chain modals); hostBridge resolves the superseded
       * request with null first so no guest promise is left hanging.
       *
       * @returns {boolean} true if the slot was claimed.
       */
      openModal: (extensionId, requestId, spec) => {
        const current = get().pendingModal;
        if (current && current.extensionId !== extensionId) return false;
        set({ pendingModal: { extensionId, requestId, spec } });
        return true;
      },

      closeModal: () => {
        set({ pendingModal: null });
      },

      registerExtension: (manifest, { enabled = true, grantedCapabilities = [], entrySource } = {}) => {
        set((state) => {
          const dismissedExtensions = { ...state.dismissedExtensions };
          delete dismissedExtensions[manifest.id]; // explicit (re)registration overrides a prior uninstall
          return {
            extensions: { ...state.extensions, [manifest.id]: manifest },
            enabled: { ...state.enabled, [manifest.id]: enabled },
            grantedCapabilities: { ...state.grantedCapabilities, [manifest.id]: grantedCapabilities },
            entrySources: entrySource
              ? { ...state.entrySources, [manifest.id]: entrySource }
              : state.entrySources,
            dismissedExtensions,
          };
        });
      },

      setEnabled: (extensionId, enabled) => {
        // For an extension whose capabilities are all-or-nothing (see
        // hasAllOrNothingCapabilities), this switch IS the capability control —
        // Settings offers no per-capability toggle for it, because every one of
        // them is required and turning one off only breaks the extension.
        // So turning it ON restores the full declared set and clears any stale
        // revoke: that is what makes the single switch a real route back for
        // someone the migration below left with a partial grant set. Turning it
        // OFF deliberately leaves grants alone — that has never been a grant
        // operation, and enforcement is unchanged either way.
        if (enabled) {
          const manifest = get().extensions[extensionId];
          if (hasAllOrNothingCapabilities(manifest)) {
            const caps = manifest.permissions.map((p) => p.capability);
            set((state) => ({
              grantedCapabilities: { ...state.grantedCapabilities, [extensionId]: caps },
              revokedCapabilities: { ...state.revokedCapabilities, [extensionId]: [] },
            }));
          }
        }
        set((state) => ({ enabled: { ...state.enabled, [extensionId]: enabled } }));
        if (!enabled) {
          // Disabling must tear down any activated panel for this extension.
          const manifest = get().extensions[extensionId];
          const panelIds = (manifest?.contributes?.panels || []).map((p) => p.id);
          set((state) => {
            const next = { ...state.activatedPanels };
            for (const id of panelIds) delete next[id];
            return { activatedPanels: next };
          });
        }
      },

      activatePanel: (panelId) => {
        // The web app has no devtools: never mount a desktop-only panel's guest
        // over the notice that replaces it (review R4).
        if (isWebApp() && isDesktopOnlyPanel(panelId)) return;
        set((state) =>
          state.activatedPanels[panelId]
            ? state
            : { activatedPanels: { ...state.activatedPanels, [panelId]: true } }
        );
      },

      deactivatePanel: (panelId) => {
        set((state) => {
          if (!(panelId in state.activatedPanels)) return state;
          const next = { ...state.activatedPanels };
          delete next[panelId];
          return { activatedPanels: next };
        });
      },

      setCapabilityGranted: (extensionId, capability, granted) => {
        set((state) => {
          const current = state.grantedCapabilities[extensionId] || [];
          const next = granted
            ? (current.includes(capability) ? current : [...current, capability])
            : current.filter((c) => c !== capability);
          // Track explicit revokes separately: absence from the grant array
          // alone can't distinguish "user turned this off" from "grants were
          // persisted before the manifest declared this cap" — bootstrap.js's
          // grant reconciliation needs that distinction to avoid undoing a
          // deliberate revoke while still healing stale grant sets.
          const currentRevoked = state.revokedCapabilities[extensionId] || [];
          const nextRevoked = granted
            ? currentRevoked.filter((c) => c !== capability)
            : (currentRevoked.includes(capability) ? currentRevoked : [...currentRevoked, capability]);
          return {
            grantedCapabilities: { ...state.grantedCapabilities, [extensionId]: next },
            revokedCapabilities: { ...state.revokedCapabilities, [extensionId]: nextRevoked },
          };
        });
      },

      // Records the manifest-declared capability set bootstrap just registered
      // for an extension — the baseline future launches diff against to detect
      // newly-declared capabilities. No-op when unchanged (avoids a persist
      // write on every launch).
      noteManifestCapsSeen: (extensionId, manifestCaps) => {
        set((state) => {
          const prev = state.knownManifestCaps[extensionId];
          if (
            Array.isArray(prev) &&
            prev.length === manifestCaps.length &&
            prev.every((c, i) => c === manifestCaps[i])
          ) {
            return state;
          }
          return { knownManifestCaps: { ...state.knownManifestCaps, [extensionId]: [...manifestCaps] } };
        });
      },

      // Records/updates the persisted marketplace-install record for a slug —
      // called after a successful install or a background version swap. The
      // manifest itself must be persisted alongside the bundle path since a
      // marketplace version's contributes/permissions can differ from the
      // bundled fallback's manifest — bootstrap re-registers from this record
      // on next launch and must not fall back to the bundled manifest.
      // Grants and the enable flag are NOT recorded here: this record is
      // device-global, and they belong to the installing account only.
      recordMarketplaceInstall: (slug, { manifest, version, bundlePath }) => {
        set((state) => ({
          installedFromMarketplace: {
            ...state.installedFromMarketplace,
            [slug]: { manifest, version, bundlePath },
          },
        }));
      },

      // Removes an extension from the store entirely, including its persisted
      // marketplace-install record (if any) — the caller is responsible for also
      // deleting the cached bundle directory via the `delete_extension_bundle`
      // Tauri command. Also marks the id as dismissed (persisted) so a bundled
      // fallback isn't silently re-registered by bootstrap.js on next launch,
      // and so a background marketplace sync doesn't silently reinstall it —
      // an explicit registerExtension() call (reinstalling from Browse
      // Marketplace) clears the dismissal.
      unregisterExtension: (extensionId) => {
        // A bundled extension's code is inside the binary; there is nothing to
        // uninstall. Removing it here would only set the hide-flag below, and
        // since Settings no longer offers "Uninstall" for bundled ids there
        // would be no surface left to clear it — the extension would vanish
        // until `undismissBundled` ran on the next launch. Refuse instead, so
        // no future call site can reintroduce that state. Disabling is the
        // supported operation: `setEnabled(id, false)`.
        if (isBundledExtension(extensionId)) {
          console.warn(
            `[extensions] "${extensionId}" is bundled with the app and cannot be uninstalled; use setEnabled() to disable it.`
          );
          return;
        }
        set((state) => {
          const manifest = state.extensions[extensionId];
          const panelIds = (manifest?.contributes?.panels || []).map((p) => p.id);
          const extensions = { ...state.extensions };
          delete extensions[extensionId];
          const enabled = { ...state.enabled };
          delete enabled[extensionId];
          const grantedCapabilities = { ...state.grantedCapabilities };
          delete grantedCapabilities[extensionId];
          // Uninstall wipes the revoke/manifest-snapshot history too — a later
          // reinstall is a fresh consent decision, not a resume.
          const revokedCapabilities = { ...state.revokedCapabilities };
          delete revokedCapabilities[extensionId];
          const knownManifestCaps = { ...state.knownManifestCaps };
          delete knownManifestCaps[extensionId];
          const entrySources = { ...state.entrySources };
          delete entrySources[extensionId];
          const activatedPanels = { ...state.activatedPanels };
          for (const id of panelIds) delete activatedPanels[id];
          const pendingModal = state.pendingModal?.extensionId === extensionId ? null : state.pendingModal;
          const installedFromMarketplace = { ...state.installedFromMarketplace };
          delete installedFromMarketplace[extensionId];
          const dismissedExtensions = { ...state.dismissedExtensions, [extensionId]: true };
          return {
            extensions,
            enabled,
            grantedCapabilities,
            revokedCapabilities,
            knownManifestCaps,
            entrySources,
            activatedPanels,
            pendingModal,
            installedFromMarketplace,
            dismissedExtensions,
          };
        });
      },
    }),
    {
      // `name` is unused by splitStorage — the two real keys are
      // `apiboo-extensions-installed` (device) and
      // `apiboo-<accountId>-extensions` (account).
      name: 'extensions',
      storage: createJSONStorage(() => splitStorage),
      // `enabled`/`grantedCapabilities` are persisted so a user's disable or
      // permission-revoke survives restart — bootstrap.js re-registers every
      // extension on launch and prefers these persisted values over manifest
      // defaults. Without this, revoking `db:mysql` was silently undone on
      // the next launch.
      partialize: (state) => ({
        installedFromMarketplace: state.installedFromMarketplace,
        dismissedExtensions: state.dismissedExtensions,
        enabled: state.enabled,
        grantedCapabilities: state.grantedCapabilities,
        // Both back bootstrap.js's grant reconciliation: revokedCapabilities
        // keeps explicit revokes authoritative across launches;
        // knownManifestCaps is the baseline for detecting capabilities newly
        // declared by an updated bundled manifest.
        revokedCapabilities: state.revokedCapabilities,
        knownManifestCaps: state.knownManifestCaps,
        railHidden: state.railHidden,
        apiClientEnabled: state.apiClientEnabled,
      }),
    }
  )
);

// On an account switch, drop the outgoing account's consent state and re-read
// the incoming account's. `installedFromMarketplace` is deliberately absent
// from the reset — the device's code inventory does not change when a
// different person signs in. bootstrap.js re-registers afterwards so the new
// account's grants are applied to the extensions already in memory.
rehydrateOnAccountChange(useExtensionStore, EMPTY_ACCOUNT_SLICE, splitStorage);

/**
 * Whether the API Client (requests, collections, environments AND History) is
 * on. The one predicate every surface checks — the rail, the title bar, the
 * history writer, and any command-palette provider (`available: () =>
 * isApiClientEnabled()`). Only an explicit `false` is off, so a profile saved
 * before the key existed keeps today's behaviour.
 */
export const selectApiClientEnabled = (state) => state?.apiClientEnabled !== false;

/** Non-React read of `selectApiClientEnabled` (stores, services, providers). */
export const isApiClientEnabled = () => selectApiClientEnabled(useExtensionStore.getState());

/** History is the API Client's request history and has no switch of its own. */
export const selectApiHistoryEnabled = selectApiClientEnabled;
export const isApiHistoryEnabled = isApiClientEnabled;

export function getEnabledExtensionEntries() {
  const { extensions, enabled } = useExtensionStore.getState();
  return Object.keys(extensions).map((id) => ({ manifest: extensions[id], enabled: !!enabled[id] }));
}

// Live hostBridge instances, keyed by extensionId. Not zustand state — bridge
// objects hold live postMessage/timeout wiring, not serializable/comparable
// data, so they're kept out of the reactive store and looked up imperatively
// by ExtensionTreeSection/ExtensionTreeNode when forwarding a user action
// (expand/click/menu-action) back to the guest that published the tree.
const bridgeRegistry = new Map();
export function registerBridge(extensionId, bridge) {
  bridgeRegistry.set(extensionId, bridge);
}
export function unregisterBridge(extensionId) {
  bridgeRegistry.delete(extensionId);
}
export function getBridge(extensionId) {
  return bridgeRegistry.get(extensionId);
}
